import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import ProtectedRoute from './ProtectedRoute';
import { LoadingBar } from '../components/common/LoadingState';
import { P } from '../utils/permissions';

import Login from '../pages/Login';
import ForgotPassword from '../pages/ForgotPassword';
import Dashboard from '../pages/Dashboard';
import Documents from '../pages/Documents';

const Shared = lazy(() => import('../pages/Shared'));
const Recent = lazy(() => import('../pages/Recent'));
const Favorites = lazy(() => import('../pages/Favorites'));
const Trash = lazy(() => import('../pages/Trash'));
const DepartmentSpace = lazy(() => import('../pages/DepartmentSpace'));
const DocumentPreviewPage = lazy(() => import('../pages/DocumentPreviewPage'));
const SearchPage = lazy(() => import('../pages/SearchPage'));
const AiSearchPage = lazy(() => import('../pages/AiSearchPage'));
const Notifications = lazy(() => import('../pages/Notifications'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const ExternalShare = lazy(() => import('../pages/ExternalShare'));
const SignDocument = lazy(() => import('../pages/SignDocument'));
const AcceptInvitation = lazy(() => import('../pages/AcceptInvitation'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Renewals = lazy(() => import('../pages/Renewals'));
const ToSign = lazy(() => import('../pages/ToSign'));
const Secrets = lazy(() => import('../pages/Secrets'));
const Pinned = lazy(() => import('../pages/Pinned'));
const ViewSecret = lazy(() => import('../pages/ViewSecret'));

const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('../pages/admin/UserManagement'));
const UserDetail = lazy(() => import('../pages/admin/UserDetail'));
const RoleManagement = lazy(() => import('../pages/admin/RoleManagement'));
const DepartmentManagement = lazy(() => import('../pages/admin/DepartmentManagement'));
const AuditLogs = lazy(() => import('../pages/admin/AuditLogs'));
const StorageOverview = lazy(() => import('../pages/admin/StorageOverview'));
const SystemSettings = lazy(() => import('../pages/admin/SystemSettings'));

const Forbidden = lazy(() => import('../pages/errors/Forbidden'));
const NotFound = lazy(() => import('../pages/errors/NotFound'));

function Fallback() {
  return (
    <div style={{ padding: 'var(--sp-6)' }}>
      <LoadingBar />
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        {/* public */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/s/:token" element={<ExternalShare />} />
        {/* No app shell: the person opening this may have no account and must see nothing else. */}
        <Route path="/sign/:token" element={<SignDocument />} />
        {/* Public: the recipient has no account, and must reach nothing else from here. */}
        <Route path="/secret/:token" element={<ViewSecret />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* application */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/:folderId" element={<Documents />} />
          <Route path="/documents/:documentId/preview" element={<DocumentPreviewPage />} />
          <Route path="/shared" element={<Shared />} />
          <Route path="/recent" element={<Recent />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/signatures" element={<ToSign />} />
          <Route path="/secrets" element={<Secrets />} />
          <Route path="/pinned" element={<Pinned />} />
          <Route path="/renewals" element={<Renewals />} />
          <Route path="/trash" element={<Trash />} />
          <Route path="/departments/:departmentId" element={<DepartmentSpace />} />
          <Route path="/departments/:departmentId/:folderId" element={<DepartmentSpace />} />
          <Route path="/search" element={<SearchPage />} />
          <Route
            path="/search/ai"
            element={
              <ProtectedRoute permission={P.SEARCH_AI}>
                <AiSearchPage />
              </ProtectedRoute>
            }
          />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* administration — each screen carries its own permission */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute permission={P.ADMIN_STORAGE_VIEW}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute permission={P.ADMIN_USER_MANAGE}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute permission={P.ADMIN_USER_MANAGE}>
                <UserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute permission={P.ADMIN_ROLE_MANAGE}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute permission={P.ADMIN_DEPARTMENT_MANAGE}>
                <DepartmentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute permission={P.ADMIN_AUDIT_VIEW}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/storage"
            element={
              <ProtectedRoute permission={P.ADMIN_STORAGE_VIEW}>
                <StorageOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute permission={P.ADMIN_SETTINGS_MANAGE}>
                <SystemSettings />
              </ProtectedRoute>
            }
          />

          <Route path="/403" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
